/**
 * @fileoverview Base theme class with common functionality.
 * All themes extend this class and override specific properties/methods.
 */

import Konva from 'konva';
import { ThemeOptions } from '../../services/ThemeService';
import type { MatchingPair } from '../../utilities/GameUtils';
import type { CustomNode, CustomShape, CustomGroup } from '../../konva-extensions';
import { PositionStrategy } from '../strategies/PositionStrategy.js';
import { ShapePositions } from "../../utilities/GameUtils";
import { IColorStrategy } from '../strategies/IColorStrategy.js';
import { VerticalPositionStrategy } from '../strategies/VerticalPositionStrategy.js';
import { RandomColorStrategy } from '../strategies/RandomColorStrategy.js';

// Basic alias for Konva nodes
export type KonvaNode = Konva.Node;

export interface ColorConfig {
    fill: string;
    stroke: string;
}

/**
 * Abstract base class for all game themes.
 * Provides common properties and methods that can be overridden by specific themes.
 * Uses strategy pattern for position and color selection.
 */
export abstract class BaseTheme {
    // Required properties that each theme must define
    abstract readonly id: string;
    abstract readonly name: string;
    abstract readonly shapeType: string;
    abstract readonly matchType: "color" | "shape" | "text" | "emoji" | "mixed" | "label";

    labels: (number | string)[];
    
    // Optional properties with defaults
    shapeTypes?: string[];
    numPairs: number;
    hasMovement: boolean;
    isPremium: boolean;
    isSequential: boolean;
    
    // Strategy pattern for positioning and colors
    protected positionStrategy: PositionStrategy;
    protected colorStrategy: IColorStrategy;

    // add constructor if needed
    constructor(options: ThemeOptions = {}) {
        this.isPremium = options.isPremium || false;
        this.numPairs = options.numPairs || 3;
        this.hasMovement = options.hasMovement || false;
        this.isSequential = options.isSequential || false;
        this.shapeTypes = options.shapeTypes;
        this.labels = options.labels || [];
        
        // Initialize strategies (can be overridden by subclasses)
        this.positionStrategy = options.positionStrategy || new VerticalPositionStrategy();
        this.colorStrategy = options.colorStrategy || new RandomColorStrategy();

        if (this.positionStrategy.isThemeValid(this) === false) {
            throw new Error(`Position strategy is not valid for theme `); // better error message is thrown in strategy implementation
        }
    }
    
    /**
     * Calculate positions for draggable and target shapes.
     * Delegates to position strategy for flexibility.
     * @param width - Canvas width
     * @param height - Canvas height
     * @param numPairs - Number of shape pairs
     * @param shapeSize - Size of the shapes (for bounds checking)
     * @returns Object with DS_POSITIONS and TS_POSITIONS arrays
     */
    getPositions(width: number, height: number, matchingPairs: MatchingPair[], shapeSize: number): ShapePositions { 
        const positions = this.positionStrategy.calculatePositions(width, height, matchingPairs, shapeSize, this.isSequential);
        return positions;
    }

    /**
     * Select colors for matching pairs.
     * Delegates to color strategy for flexibility.
     * @param availableColors - Array of available colors from CONFIG
     * @param numPairs - Number of pairs to create
     * @returns Array of selected colors
     */
    selectColors(availableColors: ColorConfig[], numPairs: number): ColorConfig[] {
        return this.colorStrategy.selectColors(availableColors, numPairs);
    }
    
    /**
     * Select labels/symbols for matching pairs.
     * Can be overridden by themes that want custom selection logic.
     * @param numPairs - Number of pairs to create
     * @returns Array of selected labels
     */
    selectLabels(numPairs: number, doShuffle: boolean = true): (number | string)[] {
        if (!this.labels || this.labels.length === 0) {
            return [];
        }
        
        // Default: randomly select without replacement
        const available = [...this.labels];
        const selected: (number | string)[] = [];

        if (!doShuffle) {
            // Sequential selection
            return available.slice(0, numPairs);
        }
        
        for (let i = 0; i < numPairs; i++) {
            const randomIndex = Math.floor(Math.random() * available.length);
            selected.push(available[randomIndex]);
            available.splice(randomIndex, 1);
        }
        
        return selected;
    }
    
    /**
     * Select shape types for matching pairs.
     * Override in mixed themes (like ShapesTheme).
     * @param numPairs - Number of pairs to create
     * @returns Array of shape types
     */
    selectShapeTypes(numPairs: number, doShuffle: boolean = true): string[] {
        if (this.shapeType !== 'mixed' || !this.shapeTypes || this.shapeTypes.length === 0) {
            return [];
        }
        
        // Default: randomly select with refill
        const selected: string[] = [];
        let available = [...this.shapeTypes];

        if (!doShuffle) {
            return available.slice(0, numPairs);
        }
        
        for (let i = 0; i < numPairs; i++) {
            if (available.length === 0) {
                available = [...this.shapeTypes];
            }
            const randomIndex = Math.floor(Math.random() * available.length);
            selected.push(available[randomIndex]);
            available.splice(randomIndex, 1);
        }
        
        return selected;
    }
    
    /**
     * Shuffle target positions for matching pairs.
     * Can be overridden by themes that want custom position assignment.
     * @param numPairs - Number of pairs to create
     * @returns Array of shuffled target indices
     */
    // shuffleTargetPositions(numPairs: number): number[] {
    //     // Default: random shuffle
    //     return Array.from({ length: numPairs }, (_, i) => i)
    //         .sort(() => Math.random() - 0.5);
    // }

    /**
     * Shuffle target positions for sequential themes.
     * Returns sequential order (0, 1, 2, ...) since all targets share same position.
     * The order determines which target appears when.
     */
    shuffleTargetPositions(numPairs: number): number[] {
        // Create array [0, 1, 2, ..., numPairs-1]
        const indices = Array.from({ length: numPairs }, (_, i) => i);
        
        // Shuffle using Fisher-Yates algorithm
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        
        return indices;
    }
    
    /**
     * Get the shape type for a specific matching pair.
     * Override in mixed themes (like ShapesTheme) that use different shape types.
     * @param _pair - The matching pair
     * @returns Shape type string (e.g., 'rect', 'circle', 'star')
     */
    getShapeTypeForPair(_pair: MatchingPair): string {
        return 'rect'; // Default shape type
    }
    
    /**
     * Get the label to pass to createShape for a specific pair.
     * @param pair - The matching pair
     * @returns Label string or undefined
     */
    getLabelForPair(pair: MatchingPair): string | undefined {
        return this.shapeType === 'text' && pair.labelValue ? String(pair.labelValue) : undefined;
    }
    
    /**
     * Apply styling to a target shape.
     * Can be overridden by themes for custom styling.
     * @param target - The target shape to style
     */
    styleTarget(target: CustomNode): void {
        // Apply styling - check if it's a Group or a Shape
        if (target.getClassName && target.getClassName() === 'Group') {
            // For groups, apply opacity to the whole group
            target.opacity(0.7);
            // Apply stroke styling to the first child (circle/shape)
            const group = target as CustomGroup;
            const children = group.getChildren();
            if (children && children.length > 0) {
                const shape = children[0] as Konva.Shape;
                shape.strokeWidth(6);
                shape.dash([12, 6]);
            }
        } else {
            // For regular shapes, apply directly
            const shape = target as CustomShape;
            shape.strokeWidth(6);
            shape.dash([12, 6]);
            target.opacity(0.7);
        }
    }
    
    /**
     * Apply styling to a draggable shape.
     * Can be overridden by themes for custom styling.
     * @param draggable - The draggable shape to style
     */
    styleDraggable(draggable: CustomNode): void {
        // Apply hitStrokeWidth only if it's not a Group
        if (!(draggable.getClassName && draggable.getClassName() === 'Group')) {
            (draggable as CustomShape).hitStrokeWidth(50);
        }
    }
    
    /**
     * Create a visual shape for this theme.
     * Must be implemented by each specific theme.
     * @param x - X position
     * @param y - Y position
     * @param size - Size of the shape
     * @param color - Color configuration
     * @param isDraggable - Whether this is a draggable shape (true) or target shape (false)
     * @param shapeTypeOrLabel - Shape type (for mixed themes) or label (for text themes)
     * @returns Konva node representing the shape
     */
    abstract createShape(
        x: number, 
        y: number, 
        size: number, 
        color: ColorConfig,
        isDraggable: boolean,
        shapeTypeOrLabel?: string | number
    ): KonvaNode;


    /**
     * Calculate responsive shape size based on screen height and theme
     * Smaller screens = smaller shapes.
     * Range: 80px to 140px.
     * @param height - Screen height in pixels
     * @param width - Screen width in pixels
     * @returns Shape size in pixels
     */
    getShapeSize(height: number, width: number): number {
        return this.positionStrategy.getShapeSize(height, width, this.numPairs);
    } 
}
